import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export interface DonutLegendEntry {
  name: string;
  value: number;
}

interface DonutLegendContentProps {
  donutData: DonutLegendEntry[];
  currentColors: string[];
  total: number;
  fontSize?: number;
  onSegmentClick?: (name: string, value: number) => void;
  scrollable?: boolean;
  truncateAt?: number;
}

// Shared Recharts <Legend content> renderer for every donut widget — a native
// <button> per row so click handling comes with keyboard support for free,
// instead of a non-native clickable <div>. `scrollable`/`truncateAt` opt in
// the CallsHandledByQueueWidget behavior (long, many-queue legends).
const DonutLegendContent: React.FC<DonutLegendContentProps> = ({
  donutData,
  currentColors,
  total,
  fontSize = 14,
  onSegmentClick,
  scrollable = false,
  truncateAt,
}) => (
  <TooltipProvider delayDuration={0}>
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        ...(scrollable ? { maxHeight: 200, overflowY: 'auto' } : {}),
      }}
    >
    {donutData.map((entry, index) => {
      const canClick = Boolean(onSegmentClick) && entry.value > 0;
      const isTruncated = Boolean(truncateAt) && entry.name.length > (truncateAt as number);
      const displayName = isTruncated ? `${entry.name.substring(0, truncateAt)}...` : entry.name;
      const color = currentColors[index % currentColors.length];

      const row = (
        <button
          type="button"
          disabled={!canClick}
          onClick={() => onSegmentClick?.(entry.name, entry.value)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: canClick ? 'pointer' : 'default',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          <span style={{ display: 'inline-block', width: 10, height: 10, flexShrink: 0, backgroundColor: color }} />
          <span style={{ color, fontSize }}>{displayName}: {entry.value}</span>
        </button>
      );

      if (!isTruncated) {
        return <React.Fragment key={entry.name}>{row}</React.Fragment>;
      }

      return (
        <Tooltip key={entry.name}>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent side="top">
            <span>{entry.name}: {entry.value}</span>
          </TooltipContent>
        </Tooltip>
      );
    })}
      <div className="text-primary dark:text-white font-semibold" style={{ fontSize }}>
        Total: {total}
      </div>
    </div>
  </TooltipProvider>
);

export default DonutLegendContent;
